import { Component, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import PageLayout from "@/components/PageLayout";
import { Button } from "@/components/ui/button";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  message?: string;
}

class BlogErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, message: error?.message };
  }

  componentDidCatch(error: Error, info: unknown) {
    // Surface for debugging without breaking the app
    console.error("[BlogErrorBoundary]", error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <PageLayout>
          <section className="mx-auto flex min-h-[60vh] max-w-2xl flex-col items-center justify-center px-6 py-24 text-center">
            <h1 className="mb-4 text-3xl font-bold text-foreground md:text-4xl">
              Something went wrong loading this article
            </h1>
            <p className="mb-8 text-muted-foreground">
              We hit an unexpected error rendering this post. The rest of the site is still working.
            </p>
            <Button asChild>
              <Link to="/blog">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Blog
              </Link>
            </Button>
          </section>
        </PageLayout>
      );
    }
    return this.props.children;
  }
}

export default BlogErrorBoundary;
